import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Paper,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import {
  MCPConnection,
  useAdminMCPConnections,
  useAdminRevokeMCPConnection,
  useAIAssistantsSetting,
  useUpdateAIAssistantsSetting,
} from '../../api/mcp';
import AdminPageHeader from './AdminPageHeader';

const SCOPE_COLORS: Record<string, 'default' | 'info' | 'warning' | 'error'> = {
  read: 'default',
  write: 'info',
  simulate: 'warning',
  delete: 'error',
  admin: 'error',
};

function formatDate(iso: string | null): string {
  if (!iso) return 'Never';
  return new Date(iso).toLocaleString();
}

export default function MCPConnectionsManagement() {
  const { data: setting, isLoading: settingLoading } = useAIAssistantsSetting();
  const updateSetting = useUpdateAIAssistantsSetting();
  const { data: connections = [], isLoading } = useAdminMCPConnections();
  const revoke = useAdminRevokeMCPConnection();
  // The hook unwraps the {data: ...} envelope already.

  const [confirmRevoke, setConfirmRevoke] = useState<MCPConnection | null>(null);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  const allow = setting?.allow_ai_assistants ?? true;

  const handleToggle = (checked: boolean) => {
    updateSetting.mutate(checked, {
      onSuccess: () =>
        setSnackbar(
          checked
            ? 'AI assistant connections enabled'
            : 'AI assistant connections disabled — existing connections stop working within a minute'
        ),
    });
  };

  const handleRevoke = () => {
    if (!confirmRevoke) return;
    revoke.mutate(confirmRevoke.id, {
      onSuccess: () => {
        setSnackbar(`Disconnected ${confirmRevoke.client_name} for ${confirmRevoke.user?.username}`);
        setConfirmRevoke(null);
      },
    });
  };

  return (
    <Box>
      <AdminPageHeader
        title="AI assistants"
        description="Whether people may connect assistants such as Claude to their Mitigate account, and who has."
        audience="admins"
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={allow}
              disabled={settingLoading || updateSetting.isPending}
              onChange={(e) => handleToggle(e.target.checked)}
            />
          }
          label="Allow people to connect AI assistants"
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 6 }}>
          Each connection acts as the person who created it — it can never do more than
          their own account allows.
        </Typography>
        {!allow && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            AI assistant connections are switched off for your organisation. Existing
            connections stop working and new ones cannot be made.
          </Alert>
        )}
      </Paper>

      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Active connections
      </Typography>
      {isLoading ? (
        <CircularProgress size={24} />
      ) : connections.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No active connections.
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Person</TableCell>
                <TableCell>Assistant</TableCell>
                <TableCell>Access</TableCell>
                <TableCell>Connected</TableCell>
                <TableCell>Last used</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {connections.map((conn) => (
                <TableRow key={conn.id}>
                  <TableCell>
                    <Tooltip title={conn.user?.email ?? ''}>
                      <span>{conn.user?.username}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{conn.client_name}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {conn.scopes.map((scope) => (
                        <Chip
                          key={scope}
                          label={scope}
                          size="small"
                          color={SCOPE_COLORS[scope] ?? 'default'}
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>{formatDate(conn.created_at)}</TableCell>
                  <TableCell>{formatDate(conn.last_used_at)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Disconnect">
                      <Button
                        size="small"
                        color="error"
                        startIcon={<LinkOffIcon />}
                        onClick={() => setConfirmRevoke(conn)}
                      >
                        Disconnect
                      </Button>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={confirmRevoke !== null} onClose={() => setConfirmRevoke(null)}>
        <DialogTitle>Disconnect AI assistant?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmRevoke?.user?.username}'s connection to{' '}
            <strong>{confirmRevoke?.client_name}</strong> will stop working
            within a minute. They can reconnect at any time (while connections
            are enabled).
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRevoke(null)}>Cancel</Button>
          <Button color="error" onClick={handleRevoke} disabled={revoke.isPending}>
            Disconnect
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar !== null}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        message={snackbar}
      />
    </Box>
  );
}
